import { Component } from 'react'

// Splitting the routes turned every in-app navigation into a network fetch. Before
// that, a page component was already in memory by the time you clicked; now it is a
// request that can fail — and on the connections this system is actually used over,
// it will. React surfaces that as a throw inside the route's Suspense boundary, and
// with nothing to catch it the error propagates to the root and unmounts the whole
// tree, so the user gets a blank white page with no way back.
//
// The common cause is not a flaky link, it is a redeploy: chunk filenames carry a
// content hash, so a session opened before a deploy asks for a hash that no longer
// exists on the server. Every subsequent navigation fails the same way until the tab
// is reloaded, which is why the reload button below is the actual fix and not a
// placeholder gesture.
const CHUNK_ERROR_PATTERN = /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i

function isChunkLoadError(error) {
  return Boolean(error) && CHUNK_ERROR_PATTERN.test(error.message || '')
}

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, errorInfo) {
    // No error reporting service is wired up in this app yet. Logging is what the
    // rest of the frontend does on failure paths, so match it rather than inventing
    // a reporting channel that nothing reads.
    console.error('[route] render failed:', error, errorInfo?.componentStack)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    const { error } = this.state

    if (!error) {
      return this.props.children
    }

    // A stale chunk is recoverable and the user did nothing wrong, so it gets its own
    // wording. Anything else is a genuine bug and should not claim a reload will help.
    const stale = isChunkLoadError(error)

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h2 className="text-lg font-medium text-gray-900">
            {stale ? 'This page needs a refresh' : 'Something went wrong'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {stale
              ? 'The application was updated while this tab was open, so part of it could no longer be loaded.'
              : 'This page failed to load. Reloading may help; if it keeps happening, report it.'}
          </p>
          <button type="button" onClick={this.handleReload} className="btn btn-primary mt-4">
            Reload
          </button>
        </div>
      </div>
    )
  }
}

export default RouteErrorBoundary
